import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import CreateAnimalForm from 'pages/private/Animal/components/CreateAnimal/CreateAnimalModal/CreateAnimalForm';
import { ModalWidthEnum } from 'components/Base/Modal';
import toast from 'components/Base/Notification';
import {
  EAnimalTypesUI,
  TAnimalFormDataUI,
  TCompleteAnimalFormDataUI,
} from 'data/types/animal.types';
import { TValidateFieldsError } from 'data/types/generalTypes';
import { EText } from 'data/datas/genericData';
import {
  animalApi,
  useCompleteAnimalMutation,
  useCreateAnimalMutation,
} from 'redux/apiSlice/animal.slice';
import useForm from 'hooks/useForm';
import { useDispatch } from 'react-redux';
import { tenantApi } from 'redux/apiSlice/tenant.slices';
import { applicationsApi } from 'redux/apiSlice/application.slice';
import { residencyApi } from 'redux/apiSlice/residency.slice';
import { modalTitle } from './CreateAnmialModal.data';
import { TCreateAnimalModalProps } from './CreateAnimalModal.types';
import AdaptiveModal from 'components/Base/Modal/AdaptiveModal/AdaptiveModal';
import { useAnimalTypeRetractionNotices } from 'components/Shared/AnimalTypeRetractionNotice/useAnimalTypeRetractionNotices';

const CreateAnimalModal = forwardRef(
  (
    {
      onCreateSuccess = () => {
        /* do nothing */
      },
      hasActiveResidency,
      residencyMoveInDate,
      residenceId,
    }: TCreateAnimalModalProps,
    ref,
  ) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [animalType, setAnimalType] = useState(EAnimalTypesUI.PET);
    const [actionType, setActionType] = useState('');
    const [navigateUrl, setNavigateUrl] = useState('');
    const [animalId, setAnimalId] = useState('');
    const { form, setCustomFieldValue } = useForm<
      TAnimalFormDataUI | TCompleteAnimalFormDataUI
    >();
    const [createAnimal, { isLoading: createLoading }] =
      useCreateAnimalMutation();
    const [completeAnimal, { isLoading: completeLoading }] =
      useCompleteAnimalMutation();
    const { showRetractionNotices, retractionNoticeModal } =
      useAnimalTypeRetractionNotices();

    useImperativeHandle(ref, () => ({
      open: (animal: EAnimalTypesUI | string, type: string, url?: string) => {
        setActionType(type);
        if (type === 'create') {
          setAnimalType(animal as EAnimalTypesUI);
          setOpen(true);
          return;
        }
        setNavigateUrl(url || '');
        setAnimalId(animal as string);
        setOpen(true);
      },
    }));

    const handleSubmit = async () => {
      try {
        if (actionType === 'create') {
          const values = (await form.validateFields()) as TAnimalFormDataUI;
          const data = await createAnimal({
            ...values,
            animalType:
              EAnimalTypesUI[animalType as keyof typeof EAnimalTypesUI],
          }).unwrap();
          // PP-10057: the animal auto-applies to every active residency, and any at a property that
          // does not manage its type comes back retracted. Hold the caller's success handling until
          // the tenant has read why, since it usually navigates away from here.
          const awaitingAcknowledgement = showRetractionNotices(
            data.animalTypeRetractionNotices,
            () => onCreateSuccess(data, animalType),
          );
          if (!awaitingAcknowledgement) {
            onCreateSuccess(data, animalType);
          }
        } else {
          const values =
            (await form.validateFields()) as TCompleteAnimalFormDataUI;
          // A field the property hides is not rendered, so it is not a registered rc-field-form
          // entity and drops out of validateFields(). /animals/complete is not a patch — the
          // backend assigns breed, microchip, aggression and house-trained unconditionally — so
          // submitting only the visible fields wipes a PMS-imported breed or a PM-entered microchip
          // and flips house-trained to a definite false. getFieldsValue(true) is the whole store,
          // which initialValues already seeded from the animal, so the hidden values round-trip.
          // Validated values win, and the convertor names the fields it sends, so nothing extra
          // travels.
          const stored = form.getFieldsValue(true) as TCompleteAnimalFormDataUI;
          const { isConvertedToPet, animalTypeRetractionNotices } =
            await completeAnimal({
              ...stored,
              ...values,
              animalId,
            }).unwrap();
          onCreateSuccess({ id: animalId, isConvertedToPet }, animalType);
          dispatch(tenantApi.util.invalidateTags(['tenant']));
          dispatch(applicationsApi.util.invalidateTags(['steps']));
          if (isConvertedToPet) {
            dispatch(animalApi.util.invalidateTags(['convertedToPet']));
          }
          // PP-10057: completing converts the animal to Pet when it is not required for a
          // disability, which retracts the application where pets are not managed. Skip the
          // hand-off to application completion entirely in that case — there is no application
          // left to complete, so sending the resident there after "Got it" would strand them.
          if (!showRetractionNotices(animalTypeRetractionNotices)) {
            if (navigateUrl) {
              navigate(navigateUrl, { state: { animalType } });
            }
          }
        }
        setOpen(false);
        dispatch(residencyApi.util.invalidateTags(['residency-with-animals']));
        toast({
          description: `Animal has been successfully ${actionType}d`,
          type: 'success',
        });
      } catch (e) {
        if (!(e as TValidateFieldsError)?.errorFields) {
          const message = get(e, 'data.message', EText.DEFAULT_ERROR) as string;
          toast({
            description: message,
            type: 'error',
          });
        }
      }
    };

    return (
      <>
        {retractionNoticeModal}
        <AdaptiveModal
          title={
            actionType === 'create'
              ? `Create ${modalTitle[animalType]}`
              : `Complete animal `
          }
          size={ModalWidthEnum.Large}
          visible={open}
          okText={capitalize(actionType)}
          cancelText="Cancel"
          onOk={handleSubmit}
          afterClose={() => {
            form.resetFields();
            setAnimalId('');
            dispatch(animalApi.util.invalidateTags(['animalById']));
          }}
          onCancel={() => {
            setOpen(false);
          }}
          confirmLoading={createLoading || completeLoading}
          okButtonProps={{ form: 'create-animal', htmlType: 'submit' }}
          destroyOnClose
        >
          <CreateAnimalForm
            animalType={animalType}
            animalId={animalId}
            actionType={actionType}
            formObj={{ form, setCustomFieldValue }}
            hasActiveResidency={hasActiveResidency}
            residencyMoveInDate={residencyMoveInDate}
            tenantResidencyId={residenceId}
          />
        </AdaptiveModal>
      </>
    );
  },
);

export default CreateAnimalModal;
